import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { deviceSpecsApi, serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import type { DeviceSpecDetail, EdgeConfigResult, EdgeDevice, EdgeLine, EdgeMachine, EdgeMachineItem, EdgeMachineItemEdit, EdgeTag, MachineItemKind, SpecMachineItem } from '../../types'
import { addressKey } from '../deviceSpec/pullMerge'
import { ITEM_SECTIONS, itemToEdit } from './shared'
import type { ItemListKey } from './shared'
import { TagRef } from './settingsUi'

// 마스터데이터 명세서 불러오기 — 명세서를 고르고([디바이스] → [명세 → Edge 적용]와 같이), 그 [마스터데이터]의 설비 하나를 골라 그 항목(전송코드 + 태그)을
// 이 Edge 설비의 Alarm·Mete·Parameter·Signal 로 나눠 넣는다.
//  · 태그는 이미 Edge 에 등록돼 있어야 한다 — 명세 디바이스 코드 + 주소로 Edge 태그를 찾는다 (없으면 넣을 수 없음)
//  · 같은 설비코드가 Edge 에 있으면 그 설비에 추가(기존 항목 유지), 없으면 고른 라인에 설비를 만든다
//  · 코드 = 전송코드, 설명 = 명세 설명. MES 설정은 그 목록의 기존 항목을 따른다 (기존 항목이 없으면 끔)
//  · 설비 생성 → 목록별 저장을 차례로 — 중간에 실패하면 거기까지만 들어간다

const KEY_OF: Record<MachineItemKind, ItemListKey> = { alarm: 'alarms', mete: 'metes', param: 'params', signal: 'signals' }

interface RowState {
  kind: MachineItemKind | ''
  alarmValue: string
}

interface MesDefault {
  mesRegisterable: boolean
  changedOnly: boolean
  mesInterval: number | null
}

// 기존 항목 따르기 — 과반이 MES 등록이면 켬. 신호 전송 방식은 MES 를 켠 기존 신호 중 가장 많은 설정
function mesDefaultOf(items: EdgeMachineItem[]): MesDefault {
  const on = items.filter(i => i.mesRegisterable)
  const mesRegisterable = items.length > 0 && on.length * 2 >= items.length
  const counts = new Map<string, { n: number; changedOnly: boolean; mesInterval: number | null }>()
  for (const i of on) {
    const changedOnly = i.changedOnly ?? true
    const mesInterval = changedOnly ? null : i.mesInterval
    const key = `${changedOnly}:${mesInterval}`
    const c = counts.get(key) ?? { n: 0, changedOnly, mesInterval }
    c.n++
    counts.set(key, c)
  }
  const top = [...counts.values()].sort((a, b) => b.n - a.n)[0]
  return { mesRegisterable, changedOnly: top?.changedOnly ?? true, mesInterval: top?.mesInterval ?? null }
}

type DoneHandler = (result: EdgeConfigResult, machineCode: string) => void

export default function SpecMasterImportDialog({ serverId, initialSpecId, onSpecChange, lines, devices, onClose, onDone }: {
  serverId: string
  initialSpecId: string | null // 이 서버에서 마지막으로 고른 명세서
  onSpecChange: (specId: string) => void
  lines: EdgeLine[]
  devices: EdgeDevice[]
  onClose: () => void
  onDone: DoneHandler
}) {
  const [specId, setSpecId] = useState(initialSpecId ?? '')
  const [saving, setSaving] = useState(false)

  const specsQuery = useQuery({
    queryKey: ['device-specs'],
    queryFn: () => deviceSpecsApi.getAll().then(r => r.data),
  })
  const specQuery = useQuery({
    queryKey: ['device-specs', specId],
    queryFn: () => deviceSpecsApi.get(specId).then(r => r.data),
    enabled: !!specId,
    staleTime: 0,
  })
  const spec = specQuery.data?.id === specId ? specQuery.data : undefined

  const pickSpec = (id: string) => {
    setSpecId(id)
    if (id) onSpecChange(id)
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={() => !saving && onClose()}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden"
        onMouseDown={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">명세서 마스터데이터 불러오기</h3>
          <select value={specId} onChange={e => pickSpec(e.target.value)} disabled={saving}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm min-w-[260px]">
            <option value="">{specsQuery.isLoading ? '불러오는 중…' : '명세서를 고르세요'}</option>
            {(specsQuery.data ?? []).map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.company ? ` · ${s.company.name}` : ''} (설비 {s.machineCount})</option>
            ))}
          </select>
          {specQuery.isFetching && <span className="text-xs text-gray-400">불러오는 중…</span>}
          <button onClick={onClose} disabled={saving} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="닫기">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!specId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">명세서를 고르면 [마스터데이터]의 설비가 나옵니다.</div>
        ) : specQuery.isError ? (
          <div className="flex-1 flex items-center justify-center text-sm text-red-500">{getErrorMessage(specQuery.error, '명세서를 불러오지 못했습니다.')}</div>
        ) : !spec ? (
          <div className="flex-1" />
        ) : (
          // 명세서를 바꾸면 처음부터
          <ImportBody key={spec.id} serverId={serverId} spec={spec} lines={lines} devices={devices}
            saving={saving} setSaving={setSaving} onClose={onClose} onDone={onDone} />
        )}
      </div>
    </div>
  )
}

function ImportBody({ serverId, spec, lines, devices, saving, setSaving, onClose, onDone }: {
  serverId: string
  spec: DeviceSpecDetail
  lines: EdgeLine[]
  devices: EdgeDevice[]
  saving: boolean
  setSaving: (saving: boolean) => void
  onClose: () => void
  onDone: DoneHandler
}) {
  const specMachines = spec.master.machines.filter(m => m.code.trim())
  const edgeMachineOf = (code: string) => {
    for (const l of lines) {
      const m = l.machines.find(x => x.code === code.trim())
      if (m) return { line: l, machine: m }
    }
    return null
  }

  const [machineKey, setMachineKey] = useState(specMachines[0]?.key ?? '')
  const specMachine = specMachines.find(m => m.key === machineKey) ?? null
  const existing = specMachine ? edgeMachineOf(specMachine.code) : null
  const [lineId, setLineId] = useState(lines[0]?.id ?? '')
  const [name, setName] = useState('')
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [checked, setChecked] = useState<string[]>([])
  const [mes, setMes] = useState<Record<MachineItemKind, MesDefault>>(() => defaultsFor(null))
  const [showErrors, setShowErrors] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  function defaultsFor(machine: EdgeMachine | null): Record<MachineItemKind, MesDefault> {
    const of = (k: MachineItemKind) => mesDefaultOf(machine?.[KEY_OF[k]] ?? [])
    return { alarm: of('alarm'), mete: of('mete'), param: of('param'), signal: of('signal') }
  }

  // 설비를 바꾸면 처음부터 — 이름 기본값은 명세 설명(없으면 설비코드), MES 기본값은 Edge 설비의 기존 항목
  useEffect(() => {
    setRows({})
    setChecked([])
    setShowErrors(false)
    setSaveError(null)
    setName(specMachine ? specMachine.name?.trim() || specMachine.description.trim() || specMachine.code.trim() : '')
    setMes(defaultsFor(existing?.machine ?? null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineKey])

  // 명세 항목 → Edge 태그 (명세 디바이스 키 → 디바이스 코드 → Edge 디바이스 → 주소)
  const tagOfItem = useMemo(() => {
    const specDevices = new Map(spec.devices.map(d => [d.key, d]))
    const edgeDevices = new Map(devices.map(d => [d.code, d]))
    return (item: SpecMachineItem): EdgeTag | null => {
      const sd = specDevices.get(item.device)
      const ed = sd ? edgeDevices.get(sd.code.trim()) : undefined
      if (!sd || !ed || !item.address.trim()) return null
      const key = addressKey(sd.channelType, item.address)
      return ed.tags.find(t => t.address && addressKey(sd.channelType, t.address) === key) ?? null
    }
  }, [spec.devices, devices])
  const specDeviceCode = (item: SpecMachineItem) => spec.devices.find(d => d.key === item.device)?.code.trim() || '-'

  // 기존 설비의 항목 — 코드는 설비 전체에서 겹치면 안 된다 (MES 전송 묶음의 키)
  const existingCodes = useMemo(() => {
    const map = new Map<string, MachineItemKind>()
    if (existing) for (const s of ITEM_SECTIONS) for (const i of existing.machine[s.key]) map.set(i.code.trim(), s.kind)
    return map
  }, [existing])

  const items = specMachine?.items ?? []
  const view = items.map(item => {
    const tag = tagOfItem(item)
    const code = item.code.trim()
    const registeredIn = existingCodes.get(code)
    const blocked = !code ? '전송코드 없음' : !tag ? 'Edge에 없는 태그' : registeredIn ? `이미 등록됨 (${titleOf(registeredIn)})` : null
    const state = rows[item.key] ?? { kind: '', alarmValue: '' }
    return { item, tag, code, blocked, ...state }
  })
  const selectable = view.filter(v => !v.blocked)

  // 오류 — 고른 행끼리 코드 중복, 같은 목록에 같은 태그(기존 항목 포함), 알람 발생 값
  const errors = useMemo(() => {
    const result: Record<string, string> = {}
    const assigned = view.filter(v => !v.blocked && v.kind)
    const codeCount = new Map<string, number>()
    assigned.forEach(v => codeCount.set(v.code, (codeCount.get(v.code) ?? 0) + 1))
    for (const v of assigned) {
      const kind = v.kind as MachineItemKind
      const sameTag = assigned.filter(x => x.kind === kind && x.tag!.id === v.tag!.id).length > 1
        || (existing?.machine[KEY_OF[kind]].some(i => i.tagId === v.tag!.id) ?? false)
      if ((codeCount.get(v.code) ?? 0) > 1) result[v.item.key] = '전송코드 중복'
      else if (sameTag) result[v.item.key] = `${titleOf(kind)}에 같은 태그가 있음`
      else if (kind === 'alarm' && !v.alarmValue.trim()) result[v.item.key] = '발생 값 필수'
    }
    return result
  }, [view, existing])
  const errorCount = Object.keys(errors).length
  const counts = ITEM_SECTIONS.map(s => ({ ...s, n: view.filter(v => !v.blocked && v.kind === s.kind).length }))
  const total = counts.reduce((n, c) => n + c.n, 0)

  const setRow = (key: string, patch: Partial<RowState>) =>
    setRows(r => ({ ...r, [key]: { ...(r[key] ?? { kind: '', alarmValue: '' }), ...patch } }))
  const assignChecked = (kind: MachineItemKind | '') => {
    setRows(r => {
      const next = { ...r }
      for (const key of checked) next[key] = { ...(next[key] ?? { kind: '', alarmValue: '' }), kind }
      return next
    })
    setChecked([])
  }
  const allChecked = selectable.length > 0 && selectable.every(v => checked.includes(v.item.key))
  const toggle = (key: string) => setChecked(c => (c.includes(key) ? c.filter(x => x !== key) : [...c, key]))

  const newMachine = !existing
  const canSave = !!specMachine && total > 0 && (!newMachine || (!!lineId && !!name.trim()))

  const save = async () => {
    setShowErrors(true)
    if (!specMachine || errorCount > 0 || !canSave) return
    setSaving(true)
    setSaveError(null)
    const machineCode = specMachine.code.trim()
    const done: string[] = []
    try {
      if (newMachine) {
        await serversApi.createMachine(serverId, {
          lineId, code: machineCode, name: name.trim(), description: specMachine.description.trim() || null,
        })
        done.push('설비 추가')
      }
      for (const { kind, title, key } of ITEM_SECTIONS) {
        const added = view.filter(v => !v.blocked && v.kind === kind)
        if (added.length === 0) continue
        const d = mes[kind]
        const list: EdgeMachineItemEdit[] = [
          ...(existing?.machine[key] ?? []).map(i => itemToEdit(i, kind)),
          ...added.map(v => ({
            tagId: v.tag!.id,
            code: v.code,
            description: v.item.description.trim() || null,
            mesRegisterable: d.mesRegisterable,
            alarmValue: kind === 'alarm' ? v.alarmValue.trim() : null,
            changedOnly: d.changedOnly,
            mesInterval: kind === 'signal' && d.mesRegisterable && !d.changedOnly ? d.mesInterval ?? 5000 : null,
          })),
        ]
        try {
          await serversApi.updateMachineItems(serverId, machineCode, kind, list)
          done.push(`${title} ${added.length}`)
        } catch (err) {
          throw new Error(`${title}: ${getErrorMessage(err, '저장하지 못했습니다.')}`)
        }
      }
      onDone({ success: true, message: `${machineCode} 명세 불러오기 완료 — ${done.join(', ')}` }, machineCode)
    } catch (err) {
      const message = err instanceof Error ? err.message : getErrorMessage(err, '저장하지 못했습니다.')
      if (done.length === 0) {
        setSaveError(message)
        setSaving(false)
      } else {
        // 일부는 들어갔다 — 목록을 다시 받도록 닫고 알린다
        onDone({ success: false, message: `일부만 저장됨 (${done.join(', ')}) — ${message}` }, machineCode)
      }
    }
  }

  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [saving])

  const toolButton = 'border border-gray-300 text-gray-600 px-2.5 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed'
  const field = 'border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm'

  return (
    <>
      {specMachines.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
          명세서 [마스터데이터]에 설비가 없습니다.
        </div>
      ) : (
        <>
          {/* 1. 명세 설비 · 라인 */}
          <div className="flex flex-wrap items-end gap-3 px-5 py-3 border-b border-gray-100 shrink-0">
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">명세 설비</span>
              <select value={machineKey} onChange={e => setMachineKey(e.target.value)} className={`${field} min-w-56`}>
                {specMachines.map(m => (
                  <option key={m.key} value={m.key}>
                    {m.code.trim()}{m.name?.trim() || m.description.trim() ? ` · ${m.name?.trim() || m.description.trim()}` : ''} ({m.items.length}){edgeMachineOf(m.code) ? ' — Edge에 있음' : ''}
                  </option>
                ))}
              </select>
            </label>
            {existing ? (
              <div className="text-xs text-gray-600 pb-2">
                Edge의 <span className="font-medium">{existing.line.name || existing.line.code}</span> ›{' '}
                <span className="font-medium">{existing.machine.name}</span> 에 추가합니다 — 기존 항목은 그대로 두고, 이미 있는 전송코드는 건너뜁니다.
              </div>
            ) : lines.length === 0 ? (
              <div className="text-xs text-amber-600 pb-2">라인이 없습니다. 먼저 "+ 라인"으로 라인을 만드세요.</div>
            ) : (
              <>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">라인</span>
                  <select value={lineId} onChange={e => setLineId(e.target.value)} className={`${field} min-w-40`}>
                    {lines.map(l => <option key={l.id} value={l.id}>{l.name || l.code}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">설비명 (새 설비) <span className="text-red-500">*</span></span>
                  <input value={name} onChange={e => setName(e.target.value)} className={`${field} w-56`} />
                </label>
              </>
            )}
          </div>

          {/* 2. 선택 → 목록 지정 */}
          <div className="flex flex-wrap items-center gap-1.5 px-5 py-2.5 border-b border-gray-100 shrink-0">
            <span className="text-xs text-gray-500 mr-1">
              {checked.length > 0 ? `선택 ${checked.length}개 →` : '행을 골라 목록을 지정하세요 →'}
            </span>
            {ITEM_SECTIONS.map(s => (
              <button key={s.kind} onClick={() => assignChecked(s.kind)} disabled={checked.length === 0} className={toolButton}>{s.title}</button>
            ))}
            <button onClick={() => assignChecked('')} disabled={checked.length === 0} className={toolButton}>빼기</button>
            <span className="ml-auto text-xs text-gray-400">
              항목 {items.length} · 넣을 수 있음 {selectable.length}
            </span>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                <tr>
                  <th className="w-9 pl-4 pr-1 py-2">
                    <input type="checkbox" checked={allChecked} disabled={selectable.length === 0} aria-label="전체 선택"
                      onChange={() => setChecked(allChecked ? [] : selectable.map(v => v.item.key))}
                      className="w-4 h-4 accent-blue-600 align-middle" />
                  </th>
                  <th className="text-left px-3 py-2">전송코드</th>
                  <th className="text-left px-3 py-2">설명</th>
                  <th className="text-left px-3 py-2">명세 태그</th>
                  <th className="text-left px-3 py-2">Edge 태그</th>
                  <th className="text-left px-3 py-2 w-36">목록</th>
                  <th className="text-left px-3 py-2 w-28">발생 값</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {view.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">이 설비에 항목이 없습니다.</td></tr>
                ) : view.map(v => {
                  const error = showErrors ? errors[v.item.key] : undefined
                  return (
                    <tr key={v.item.key} className={v.blocked ? 'opacity-50' : checked.includes(v.item.key) ? 'bg-blue-50' : ''}>
                      <td className="pl-4 pr-1 py-2">
                        <input type="checkbox" checked={checked.includes(v.item.key)} disabled={!!v.blocked}
                          onChange={() => toggle(v.item.key)} aria-label="선택" className="w-4 h-4 accent-blue-600" />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">
                        {v.code || '-'}
                        {(v.blocked || error) && <div className={`text-[11px] font-sans mt-0.5 ${error ? 'text-red-600' : 'text-gray-500'}`}>{error ?? v.blocked}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{v.item.description || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{specDeviceCode(v.item)} · {v.item.address || '-'}</td>
                      <td className="px-3 py-2">{v.tag ? <TagRef code={null} tag={v.tag} /> : <span className="text-xs text-gray-300">-</span>}</td>
                      <td className="px-3 py-2">
                        <select value={v.kind} disabled={!!v.blocked} onChange={e => setRow(v.item.key, { kind: e.target.value as MachineItemKind | '' })}
                          className={`border rounded-md px-2 py-1 text-xs w-full ${v.kind ? 'border-blue-300 text-blue-700' : 'border-gray-300 text-gray-400'}`}>
                          <option value="">넣지 않음</option>
                          {ITEM_SECTIONS.map(s => <option key={s.kind} value={s.kind}>{s.title}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {v.kind === 'alarm' && (
                          <input value={v.alarmValue} onChange={e => setRow(v.item.key, { alarmValue: e.target.value })}
                            aria-invalid={error === '발생 값 필수'}
                            className={`w-full border rounded-md px-2 py-1 text-xs font-mono ${error === '발생 값 필수' ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* 목록별 개수 · MES 기본값 (기존 항목을 따름 — 바꿀 수 있음) */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-2.5 border-t border-gray-100 bg-gray-50/60 shrink-0">
            {counts.map(c => (
              <span key={c.kind} className={`flex items-center gap-2 text-xs ${c.n > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                <span className="font-medium">{c.title} {c.n}</span>
                <label className="flex items-center gap-1" title="새로 넣는 항목의 MES 등록 — 이 목록의 기존 항목을 따릅니다">
                  <input type="checkbox" checked={mes[c.kind].mesRegisterable} disabled={c.n === 0}
                    onChange={e => setMes(m => ({ ...m, [c.kind]: { ...m[c.kind], mesRegisterable: e.target.checked } }))}
                    className="w-3.5 h-3.5 accent-blue-600" />
                  MES
                </label>
                {c.kind === 'signal' && mes.signal.mesRegisterable && c.n > 0 && (
                  <span className="text-gray-400">({mes.signal.changedOnly ? '변경 시' : `${mes.signal.mesInterval ?? 5000}ms 주기`})</span>
                )}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
            {saveError ? (
              <span className="text-xs text-red-600">{saveError}</span>
            ) : showErrors && errorCount > 0 ? (
              <span className="text-xs text-red-600">확인이 필요한 항목 {errorCount}개</span>
            ) : (
              <span className="text-xs text-gray-400">코드 = 전송코드, 설명 = 명세 설명. MES 설정은 각 목록의 기존 항목을 따르고, 저장하면 엣지에 바로 반영됩니다.</span>
            )}
            <div className="ml-auto flex gap-2">
              <button onClick={onClose} disabled={saving}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
                취소
              </button>
              <button onClick={save} disabled={saving || !canSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
                {saving ? '저장 중…' : total > 0 ? `${total}개 저장` : '저장'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

const titleOf = (kind: MachineItemKind) => ITEM_SECTIONS.find(s => s.kind === kind)?.title ?? kind
